import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  DEFAULT_JURISDICTIONS_CONFIG,
  DEFAULT_VERSIONS,
  type ComplianceVersions,
  type JurisdictionsConfig,
} from "./constants";

async function isAdmin(userId: string): Promise<boolean> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  return (data ?? []).some((r) => r.role === "admin");
}

/** Public — anyone can read the active jurisdiction policy + versions. */
export const getCompliancePublicConfig = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("site_settings")
    .select("key, value")
    .in("key", ["compliance_jurisdictions", "compliance_versions"]);
  const map = new Map((data ?? []).map((r) => [r.key, r.value as unknown]));
  const jurisdictions =
    (map.get("compliance_jurisdictions") as JurisdictionsConfig | undefined) ??
    DEFAULT_JURISDICTIONS_CONFIG;
  const versions =
    (map.get("compliance_versions") as ComplianceVersions | undefined) ?? DEFAULT_VERSIONS;
  return { jurisdictions, versions };
});

const JurisdictionsSchema = z.object({
  mode: z.enum(["warn", "block"]),
  warning: z.array(z.string().regex(/^[A-Z]{2}$/)).max(250),
  blocked: z.array(z.string().regex(/^[A-Z]{2}$/)).max(250),
});

const VersionsSchema = z.object({
  risk_disclosure: z.string().trim().min(1).max(32),
  disclaimer: z.string().trim().min(1).max(32),
  terms: z.string().trim().min(1).max(32),
});

export const updateJurisdictionsConfig = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => JurisdictionsSchema.parse(i))
  .handler(async ({ data, context }) => {
    if (!(await isAdmin(context.userId))) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const value = {
      mode: data.mode,
      warning: Array.from(new Set(data.warning.map((c) => c.toUpperCase()))),
      blocked: Array.from(new Set(data.blocked.map((c) => c.toUpperCase()))),
    };
    const { error } = await supabaseAdmin
      .from("site_settings")
      .upsert({
        key: "compliance_jurisdictions",
        value: JSON.parse(JSON.stringify(value)) as never,
        updated_at: new Date().toISOString(),
      });
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("audit_log").insert({
      actor_id: context.userId,
      action: "compliance.jurisdictions.updated",
      target_type: "site_settings",
      target_id: "compliance_jurisdictions",
      metadata: value as never,
    });
    return { ok: true };
  });

export const updateComplianceVersions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) => VersionsSchema.parse(i))
  .handler(async ({ data, context }) => {
    if (!(await isAdmin(context.userId))) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const value: ComplianceVersions = { ...data, updated_at: new Date().toISOString() };
    const { error } = await supabaseAdmin
      .from("site_settings")
      .upsert({
        key: "compliance_versions",
        value: JSON.parse(JSON.stringify(value)) as never,
        updated_at: new Date().toISOString(),
      });
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("audit_log").insert({
      actor_id: context.userId,
      action: "compliance.versions.updated",
      target_type: "site_settings",
      target_id: "compliance_versions",
      metadata: value as never,
    });
    return { ok: true };
  });

/** Admin — recent suitability acknowledgments from audit_log. */
export const listSuitabilityAcknowledgments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    if (!(await isAdmin(context.userId))) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("audit_log")
      .select("id, actor_id, actor_email, action, metadata, created_at")
      .in("action", ["suitability.confirmed", "jurisdiction.acknowledged"])
      .order("created_at", { ascending: false })
      .limit(100);
    return { entries: data ?? [] };
  });

/** Admin — users whose accepted risk_disclosure_version is behind current. */
export const listStaleAcceptances = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    if (!(await isAdmin(context.userId))) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: vsetting } = await supabaseAdmin
      .from("site_settings")
      .select("value")
      .eq("key", "compliance_versions")
      .maybeSingle();
    const versions =
      (vsetting?.value as ComplianceVersions | undefined) ?? DEFAULT_VERSIONS;
    const { data } = await supabaseAdmin
      .from("profiles")
      .select(
        "id, email, suitability_confirmed_at, risk_disclosure_version, disclaimer_version, terms_version",
      )
      .order("suitability_confirmed_at", { ascending: false, nullsFirst: false })
      .limit(500);
    const stale = (data ?? []).filter(
      (p) =>
        p.suitability_confirmed_at &&
        (p.risk_disclosure_version !== versions.risk_disclosure ||
          p.disclaimer_version !== versions.disclaimer),
    );
    return { current: versions, users: stale };
  });
